/**
 * Smart placement detection (Agent Air, chroma + placement slice) — pure,
 * client-side, no DOM, no deps.
 *
 * After the chroma key removes the keyed color (see `chroma.applyChromaKey`,
 * which sets matched pixels to alpha 0), this module AUTO-DETECTS the resulting
 * transparent region and fits the design-placement geometry: a perspective
 * QUADRILATERAL (four ordered corners) a design can be warped into. This is the
 * "cut-out / print-area" placement, distinct from the camera cutout.
 *
 * Robust to ANY angle:
 *  - lay-flat  → axis-aligned rectangle (min-area rect with ~0° tilt),
 *  - tilted    → rotated rectangle (min-area rect via rotating calipers),
 *  - isometric → general convex quad / perspective (max-area inscribed quad of
 *                the region's convex hull, chosen when it fits materially better
 *                than the rotated rect).
 *
 * Pipeline: transparent mask → largest connected region (chroma.largestRegion) →
 * boundary points → convex hull (monotone chain) → candidate quads (min-area
 * rect + best 4 hull vertices by area) → pick the higher-coverage candidate →
 * order corners TL,TR,BR,BL → normalize to 0..1. Everything is deterministic and
 * unit-tested on synthetic alpha grids at multiple angles.
 */
import type { Point, Polygon } from "@genz/engine-bind";
import { largestRegion, type PixelBBox } from "./chroma.js";

/** A four-corner placement quad, ordered TL, TR, BR, BL (clockwise, y-down). */
export type Quad = [Point, Point, Point, Point];

/**
 * Build a boolean mask (row-major, `width*height`) of FULLY TRANSPARENT pixels
 * (alpha === 0) from an RGBA buffer — the region the chroma key removed.
 */
export function transparentMask(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
): Uint8Array {
  const grid = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    if (rgba[i * 4 + 3] === 0) grid[i] = 1;
  }
  return grid;
}

/**
 * Boundary cells of a binary region mask: every set cell that touches at least
 * one unset cell (or the image edge) in its 4-neighborhood. Each is emitted as
 * BOTH grid corners spanning the cell would be overkill; we emit the cell's
 * center-ish corner points (the four corners of the cell) so the hull wraps the
 * true outer extent of the region rather than cell centers.
 */
function boundaryCorners(mask: Uint8Array, width: number, height: number): Point[] {
  const at = (x: number, y: number) => x >= 0 && y >= 0 && x < width && y < height && mask[y * width + x] === 1;
  const pts: Point[] = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (mask[y * width + x] !== 1) continue;
      const edge = !at(x - 1, y) || !at(x + 1, y) || !at(x, y - 1) || !at(x, y + 1);
      if (!edge) continue;
      // The four corners of this cell (cell (x,y) spans [x,x+1]×[y,y+1]).
      pts.push({ x, y }, { x: x + 1, y }, { x, y: y + 1 }, { x: x + 1, y: y + 1 });
    }
  }
  return pts;
}

/** `mask` with its holes filled: openings inside a print area (camera cut-outs) are not its outline. */
function filledHoles(mask: Uint8Array, width: number, height: number): Uint8Array {
  const outside = new Uint8Array(mask.length);
  const stack: number[] = [];
  const reach = (n: number) => {
    if (mask[n] === 1 || outside[n]) return;
    outside[n] = 1;
    stack.push(n);
  };
  for (let x = 0; x < width; x++) { reach(x); reach((height - 1) * width + x); }
  for (let y = 0; y < height; y++) { reach(y * width); reach(y * width + width - 1); }
  while (stack.length) {
    const n = stack.pop()!;
    const x = n % width;
    if (x > 0) reach(n - 1);
    if (x < width - 1) reach(n + 1);
    if (n >= width) reach(n - width);
    if (n < mask.length - width) reach(n + width);
  }
  return outside.map((o) => 1 - o);
}

/** Cross product (b-a)×(c-a); >0 = counter-clockwise turn in a y-down raster. */
function cross(a: Point, b: Point, c: Point): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

/**
 * Convex hull (Andrew's monotone chain). Returns hull vertices in CCW order
 * (y-down space), without the duplicated closing point. Deterministic.
 */
export function convexHull(points: Point[]): Point[] {
  if (points.length < 3) return points.map((p) => ({ ...p }));
  const pts = [...points].sort((p, q) => (p.x === q.x ? p.y - q.y : p.x - q.x));
  // Dedupe exact coincidents to keep the hull stable.
  const uniq: Point[] = [];
  for (const p of pts) {
    const last = uniq[uniq.length - 1];
    if (!last || last.x !== p.x || last.y !== p.y) uniq.push(p);
  }
  if (uniq.length < 3) return uniq;

  const lower: Point[] = [];
  for (const p of uniq) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Point[] = [];
  for (let i = uniq.length - 1; i >= 0; i--) {
    const p = uniq[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/** Absolute polygon area (shoelace). */
function polyArea(poly: Point[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a) / 2;
}

/**
 * Minimum-area bounding rectangle of a convex hull via rotating calipers: try
 * every hull edge as a candidate rectangle orientation and keep the smallest.
 * Returns its 4 corners (arbitrary order). Handles lay-flat (axis-aligned, the
 * winning edge is horizontal) and tilted (rotated) cases EXACTLY for a rectangle
 * region, and gives a tight oriented box for any convex blob.
 */
export function minAreaRect(hull: Point[]): Quad | null {
  if (hull.length < 3) return null;
  let best: { area: number; corners: Quad } | null = null;
  const n = hull.length;
  for (let i = 0; i < n; i++) {
    const a = hull[i];
    const b = hull[(i + 1) % n];
    let ex = b.x - a.x;
    let ey = b.y - a.y;
    const len = Math.hypot(ex, ey);
    if (len < 1e-9) continue;
    ex /= len;
    ey /= len; // unit edge direction (u); normal v = (-ey, ex)
    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    for (const p of hull) {
      const u = p.x * ex + p.y * ey;
      const v = -p.x * ey + p.y * ex;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
    const area = (maxU - minU) * (maxV - minV);
    if (!best || area < best.area) {
      // Reconstruct the four corners from (u,v) extremes back into x,y.
      const toXY = (u: number, v: number): Point => ({ x: u * ex - v * ey, y: u * ey + v * ex });
      best = {
        area,
        corners: [toXY(minU, minV), toXY(maxU, minV), toXY(maxU, maxV), toXY(minU, maxV)],
      };
    }
  }
  return best?.corners ?? null;
}

/**
 * Best inscribed quadrilateral of a convex hull: the 4 hull vertices that
 * maximize enclosed area. For a hull with many vertices this is the robust way
 * to recover a perspective/isometric quad (a rotated rect's hull is ~4 points so
 * this just returns them; an isometric trapezoid's hull gives the trapezoid).
 *
 * Uses the standard O(n²)-ish max-area-quad search: for each pair of opposite
 * "diagonal" vertices (i,k), find the best j in (i,k) and best l in (k,i) by the
 * triangle-area maximization that together bound the largest quad. Deterministic.
 */
export function maxAreaQuad(hull: Point[]): Quad | null {
  const n = hull.length;
  if (n < 3) return null;
  if (n === 3) {
    // Degenerate to a triangle expressed as a quad (duplicate the nearest edge
    // midpoint) so downstream always has 4 corners; area-wise still the triangle.
    const [a, b, c] = hull;
    const mid = { x: (c.x + a.x) / 2, y: (c.y + a.y) / 2 };
    return [a, b, c, mid];
  }
  const triArea = (p: Point, q: Point, r: Point) => Math.abs(cross(p, q, r)) / 2;
  let best: { area: number; quad: Quad } | null = null;
  // i<k are the diagonal endpoints; j between them, l on the other arc.
  for (let i = 0; i < n; i++) {
    for (let k = i + 2; k < n; k++) {
      // Best j in (i,k): maximize triangle (i,j,k).
      let bj = -1;
      let bjA = -1;
      for (let j = i + 1; j < k; j++) {
        const a = triArea(hull[i], hull[j], hull[k]);
        if (a > bjA) {
          bjA = a;
          bj = j;
        }
      }
      // Best l in (k,i) wrapping past n: maximize triangle (k,l,i).
      let bl = -1;
      let blA = -1;
      for (let off = 1; off < n - (k - i); off++) {
        const l = (k + off) % n;
        if (l === i) break;
        const a = triArea(hull[k], hull[l], hull[i]);
        if (a > blA) {
          blA = a;
          bl = l;
        }
      }
      if (bj < 0 || bl < 0) continue;
      const quad: Quad = [hull[i], hull[bj], hull[k], hull[bl]];
      const area = polyArea(quad);
      if (!best || area > best.area) best = { area, quad };
    }
  }
  return best?.quad ?? null;
}

/**
 * Order four corners as TL, TR, BR, BL in a y-down image space (clockwise):
 * the two with the smallest y are the top pair (left = smaller x), the two with
 * the largest y are the bottom pair. Robust for rotated/skewed quads because it
 * splits by the centroid rather than assuming axis alignment.
 */
export function orderCorners(corners: Point[]): Quad {
  const cx = corners.reduce((s, p) => s + p.x, 0) / corners.length;
  const cy = corners.reduce((s, p) => s + p.y, 0) / corners.length;
  // Sort by angle from centroid, then rotate so the first corner is the
  // top-left-most (smallest x+y). Angles increase clockwise in y-down space.
  const byAngle = [...corners].sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx));
  let startIdx = 0;
  let bestScore = Infinity;
  for (let i = 0; i < byAngle.length; i++) {
    const score = byAngle[i].x + byAngle[i].y; // smallest = top-left
    if (score < bestScore) {
      bestScore = score;
      startIdx = i;
    }
  }
  const ordered = [
    byAngle[startIdx % 4],
    byAngle[(startIdx + 1) % 4],
    byAngle[(startIdx + 2) % 4],
    byAngle[(startIdx + 3) % 4],
  ];
  return ordered as Quad;
}

// --- sub-pixel corner refinement -------------------------------------------

interface Line {
  p: Point;
  d: Point;
} // a point on the line + a unit direction

/** Distance from point `p` to segment a–b (clamped). */
function distToSegment(p: Point, a: Point, b: Point): number {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const len2 = vx * vx + vy * vy;
  let t = len2 > 1e-12 ? ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy));
}

/** Total-least-squares (PCA) line fit through points; null if too few/degenerate. */
function fitLine(points: Point[]): Line | null {
  const n = points.length;
  if (n < 2) return null;
  let mx = 0;
  let my = 0;
  for (const p of points) {
    mx += p.x;
    my += p.y;
  }
  mx /= n;
  my /= n;
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (const p of points) {
    const dx = p.x - mx;
    const dy = p.y - my;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }
  if (sxx + syy < 1e-12) return null;
  // Principal direction = eigenvector of the larger eigenvalue of the 2×2 covariance.
  const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  return { p: { x: mx, y: my }, d: { x: Math.cos(theta), y: Math.sin(theta) } };
}

/** Intersection of two lines; null when near-parallel. */
function lineIntersect(l1: Line, l2: Line): Point | null {
  const denom = l1.d.x * l2.d.y - l1.d.y * l2.d.x;
  if (Math.abs(denom) < 1e-9) return null;
  const dx = l2.p.x - l1.p.x;
  const dy = l2.p.y - l1.p.y;
  const t = (dx * l2.d.y - dy * l2.d.x) / denom;
  return { x: l1.p.x + t * l1.d.x, y: l1.p.y + t * l1.d.y };
}

/**
 * `line` moved outward, parallel, until none of `pts` lies outside it. The design fills the quad
 * exactly, so an edge fitted through the middle of a slightly bowed outline would leave the
 * outline's outer slivers without design.
 */
function enclosing(line: Line | null, pts: Point[], centre: Point): Line | null {
  if (!line) return null;
  let n = { x: -line.d.y, y: line.d.x };
  if (n.x * (line.p.x - centre.x) + n.y * (line.p.y - centre.y) < 0) n = { x: -n.x, y: -n.y };
  const out = pts.reduce((m, p) => Math.max(m, n.x * (p.x - line.p.x) + n.y * (p.y - line.p.y)), 0);
  return { p: { x: line.p.x + n.x * out, y: line.p.y + n.y * out }, d: line.d };
}

/**
 * Refine a cyclic-ordered quad's corners to sub-pixel accuracy: assign every
 * boundary point to its nearest quad edge, fit a TLS line to each edge, and
 * re-derive each corner as the intersection of its two adjacent edge lines.
 * Averaging many boundary points per edge cancels the ±1px raster quantization
 * that a vertex-only fit carries. Each corner is accepted only when both edges
 * have enough support AND the shift stays small (a guard against concave /
 * strong-perspective surprises); otherwise the original corner is kept.
 */
export function refineQuadCorners(quad: Quad, boundary: Point[]): Quad {
  const edges = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 0],
  ] as const;
  const diag = Math.hypot(quad[0].x - quad[2].x, quad[0].y - quad[2].y);
  const maxShift = 0.2 * diag; // a refined corner may not jump more than 20% of the diagonal
  const buckets: Point[][] = [[], [], [], []];
  for (const pt of boundary) {
    let bi = 0;
    let bd = Infinity;
    for (let e = 0; e < 4; e++) {
      const d = distToSegment(pt, quad[edges[e][0]], quad[edges[e][1]]);
      if (d < bd) {
        bd = d;
        bi = e;
      }
    }
    buckets[bi].push(pt);
  }
  const centre = { x: (quad[0].x + quad[1].x + quad[2].x + quad[3].x) / 4, y: (quad[0].y + quad[1].y + quad[2].y + quad[3].y) / 4 };
  const lines = buckets.map((pts) => (pts.length >= 3 ? enclosing(fitLine(pts), pts, centre) : null));
  const out: Quad = [quad[0], quad[1], quad[2], quad[3]];
  for (let c = 0; c < 4; c++) {
    const lPrev = lines[(c + 3) % 4]; // edge ending at corner c
    const lCur = lines[c]; // edge starting at corner c
    if (!lPrev || !lCur) continue;
    const x = lineIntersect(lPrev, lCur);
    if (x && Math.hypot(x.x - quad[c].x, x.y - quad[c].y) <= maxShift) out[c] = x;
  }
  return out;
}

/** Grayscale (luma) buffer from RGBA — input for gradient edge-snap. */
export function toGray(rgba: Uint8ClampedArray | Uint8Array | number[], width: number, height: number): Uint8Array {
  const g = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    g[i] = (0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2]) | 0;
  }
  return g;
}

/** Sobel gradient magnitude at integer (x,y); 0 at the border. */
function gradMag(g: Uint8Array, w: number, h: number, x: number, y: number): number {
  if (x <= 0 || y <= 0 || x >= w - 1 || y >= h - 1) return 0;
  const at = (xx: number, yy: number) => g[yy * w + xx];
  const gx = at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1)
    - at(x - 1, y - 1) - 2 * at(x - 1, y) - at(x - 1, y + 1);
  const gy = at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1)
    - at(x - 1, y - 1) - 2 * at(x, y - 1) - at(x + 1, y - 1);
  return Math.abs(gx) + Math.abs(gy);
}

/**
 * Gradient edge-snap: nudge each quad corner to the pixel with the strongest edge
 * gradient within ±`search` px, so the print-area corners lock onto the real case
 * edge instead of floating just inside/outside it. Pure + deterministic; pixel
 * coordinates in/out. Corners only move if a clearly stronger edge is found nearby.
 */
export function snapQuadToGradient(
  quad: Quad,
  gray: Uint8Array,
  width: number,
  height: number,
  search = 4,
): Quad {
  const snapPt = (p: Point): Point => {
    const cx = Math.round(p.x);
    const cy = Math.round(p.y);
    let best = gradMag(gray, width, height, cx, cy);
    let bx = cx;
    let by = cy;
    for (let dy = -search; dy <= search; dy++) {
      for (let dx = -search; dx <= search; dx++) {
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= width || y >= height) continue;
        const m = gradMag(gray, width, height, x, y);
        // Prefer a stronger edge; tie-break toward the original (smaller shift).
        if (m > best) { best = m; bx = x; by = y; }
      }
    }
    return { x: bx, y: by };
  };
  return [snapPt(quad[0]), snapPt(quad[1]), snapPt(quad[2]), snapPt(quad[3])];
}

/** Normalize a pixel-space quad to a 0..1 polygon (clamped into the unit box). */
function normalizeQuad(quad: Quad, width: number, height: number): Polygon {
  const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
  return quad.map((p) => ({ x: clamp01(p.x / width), y: clamp01(p.y / height) }));
}

/** Pixel quad covering the bbox (lay-flat fallback). */
function bboxQuad(b: PixelBBox): Quad {
  return [
    { x: b.minX, y: b.minY },
    { x: b.maxX + 1, y: b.minY },
    { x: b.maxX + 1, y: b.maxY + 1 },
    { x: b.minX, y: b.maxY + 1 },
  ];
}

export interface PlacementResult {
  /** The placement quad as a normalized 0..1 polygon (4 points, TL,TR,BR,BL). */
  quad: Polygon;
  /**
   * How the geometry was derived: `"rect"` (lay-flat / tilted rectangle, from the
   * min-area rect) or `"quad"` (isometric / perspective, from the max-area
   * inscribed quad), or `"bbox"` (degenerate fallback).
   */
  kind: "rect" | "quad" | "bbox";
  /** Fraction of the region's hull area the chosen quad covers (0..1 quality). */
  coverage: number;
}

/**
 * Detect the transparent region in an RGBA buffer and fit a placement quad.
 *
 * Returns null when there is no transparent region (chroma not applied yet, or
 * nothing removed). The result's `kind` reports whether a rectangle (lay-flat /
 * tilted) or a general quad (isometric / perspective) fit best, and `coverage`
 * is a 0..1 quality signal (the chosen quad's area ÷ the region hull's area).
 *
 * `rectSlack` (default 0.08) is how much wasted area the min-area rectangle is
 * allowed before we switch to the inscribed quad. A true rectangle (lay-flat or
 * tilted) has rect area ≈ hull area → stays a `"rect"`. An isometric trapezoid
 * has a min-area rect that overshoots its hull by well over 8% → switches to a
 * general `"quad"`. Raise it to favor rectangles; lower it to favor quads.
 */
export function detectTransparentRegion(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
  rectSlack = 0.08,
): PlacementResult | null {
  if (width <= 0 || height <= 0) return null;
  const mask = transparentMask(rgba, width, height);
  const region = largestRegion(mask, width, height);
  if (!region) return null;

  const boundary = boundaryCorners(filledHoles(region.mask, width, height), width, height);
  const hullPts = convexHull(boundary);
  const hullA = polyArea(hullPts);

  // Candidate A: tight oriented rectangle (lay-flat / tilted).
  const rect = minAreaRect(hullPts);
  const rectA = rect ? polyArea(rect) : Infinity;
  // Candidate B: best inscribed quad (isometric / perspective).
  const quad = maxAreaQuad(hullPts);
  const quadA = quad ? polyArea(quad) : 0;

  // How tightly each candidate matches the region's convex hull. The rectangle
  // circumscribes the hull (rectA ≥ hullA) → tightness = hull/rect ≤ 1, and = 1
  // for a true rectangle. The inscribed quad is ⊆ hull (quadA ≤ hullA) →
  // tightness = quad/hull ≤ 1, and = 1 when the hull itself is a 4-gon.
  const rectTightness = hullA > 0 && isFinite(rectA) ? Math.min(1, hullA / Math.max(rectA, 1e-9)) : 0;
  const quadTightness = hullA > 0 ? Math.min(1, quadA / hullA) : 0;

  let chosen: Quad;
  let kind: PlacementResult["kind"];
  let coverage: number;
  // Prefer the rectangle whenever it isn't wasting more than `rectSlack` of area
  // (i.e. the region really is a rectangle at some angle). Otherwise, if the
  // inscribed quad explains the hull well, use it (isometric / perspective).
  if (rect && rectTightness >= 1 - rectSlack) {
    chosen = rect;
    kind = "rect";
    coverage = rectTightness;
  } else if (quad && quadTightness > 0.5) {
    chosen = quad;
    kind = "quad";
    coverage = quadTightness;
  } else if (rect) {
    chosen = rect;
    kind = "rect";
    coverage = rectTightness;
  } else {
    chosen = bboxQuad(region.bbox);
    kind = "bbox";
    coverage = 1;
  }

  // Sub-pixel refine the rectangle / perspective quad against the boundary
  // points (the bbox fallback is already exact to the raster extent).
  if (kind !== "bbox") chosen = refineQuadCorners(chosen, boundary);

  return { quad: normalizeQuad(orderCorners(chosen), width, height), kind, coverage };
}

/** A keyed piece smaller than this share of the largest is noise, not print area. */
const MIN_PIECE_SHARE = 0.02;
/** Keyed share of its box (minus the camera hole) at which a panel reads as front-on. */
const FRONT_ON_FILL = 0.85;
/** How much of its own box a hole must fill to be a camera island, not a perspective corner. */
const RECT_HOLE_FILL = 0.75;

interface Component { size: number; bbox: PixelBBox }

/** Every 4-connected component of set cells, with its size and bounding box. */
function components(grid: Uint8Array, width: number, height: number): Component[] {
  const seen = new Uint8Array(grid.length);
  const out: Component[] = [];
  const stack: number[] = [];
  const visit = (n: number) => {
    if (grid[n] && !seen[n]) { seen[n] = 1; stack.push(n); }
  };
  for (let start = 0; start < grid.length; start++) {
    if (!grid[start] || seen[start]) continue;
    const bbox: PixelBBox = { minX: width, minY: height, maxX: -1, maxY: -1 };
    let size = 0;
    seen[start] = 1;
    stack.push(start);
    while (stack.length) {
      const i = stack.pop()!;
      const x = i % width;
      const y = (i - x) / width;
      size++;
      bbox.minX = Math.min(bbox.minX, x); bbox.maxX = Math.max(bbox.maxX, x);
      bbox.minY = Math.min(bbox.minY, y); bbox.maxY = Math.max(bbox.maxY, y);
      if (x > 0) visit(i - 1);
      if (x < width - 1) visit(i + 1);
      if (y > 0) visit(i - width);
      if (y < height - 1) visit(i + width);
    }
    out.push({ size, bbox });
  }
  return out;
}

const boxArea = (b: PixelBBox) => (b.maxX - b.minX + 1) * (b.maxY - b.minY + 1);

/** Does the keyed area fill its box once the largest rectangular hole (the camera) is taken out? */
function isFrontOn(mask: Uint8Array, width: number, box: PixelBBox): boolean {
  const w = box.maxX - box.minX + 1;
  const h = box.maxY - box.minY + 1;
  const open = new Uint8Array(w * h);
  let keyed = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask[(box.minY + y) * width + box.minX + x]) keyed++;
      else open[y * w + x] = 1;
    }
  }
  const hole = Math.max(0, ...components(open, w, h)
    .filter((c) => c.size >= RECT_HOLE_FILL * boxArea(c.bbox)).map((c) => c.size));
  return keyed >= FRONT_ON_FILL * (w * h - hole);
}

/**
 * The placement quad of a KEYED print area. A front-on back panel is a rectangle minus the
 * camera island (an L, or two pieces split by a camera bar): its quad is the union box of every
 * keyed piece, and the island stays excluded by the keyed alpha rather than by bending the quad.
 * An angled panel keeps the fitted quad, whose perspective the box would lose.
 */
export function keyedPrintQuad(
  rgba: Uint8ClampedArray | Uint8Array | number[],
  width: number,
  height: number,
): Polygon | null {
  if (width <= 0 || height <= 0) return null;
  const mask = transparentMask(rgba, width, height);
  const pieces = components(mask, width, height);
  const largest = Math.max(0, ...pieces.map((p) => p.size));
  if (!largest) return null;
  const kept = pieces.filter((p) => p.size >= MIN_PIECE_SHARE * largest).map((p) => p.bbox);
  const box: PixelBBox = {
    minX: Math.min(...kept.map((b) => b.minX)), minY: Math.min(...kept.map((b) => b.minY)),
    maxX: Math.max(...kept.map((b) => b.maxX)), maxY: Math.max(...kept.map((b) => b.maxY)),
  };
  return isFrontOn(mask, width, box)
    ? normalizeQuad(bboxQuad(box), width, height)
    : detectTransparentRegion(rgba, width, height)?.quad ?? null;
}

/** `keyedPrintQuad` for a row-major print mask (255 = inside the print region). */
export function printQuadFromAlpha(
  printAlpha: Uint8Array | number[],
  width: number,
  height: number,
): PlacementResult["quad"] | null {
  if (width <= 0 || height <= 0) return null;
  const rgba = new Uint8Array(width * height * 4);
  for (let i = 0; i < width * height; i++) {
    rgba[i * 4 + 3] = (printAlpha[i] ?? 0) >= 128 ? 0 : 255;
  }
  return keyedPrintQuad(rgba, width, height);
}
